import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import classNames from 'classnames/bind';
import _ from 'lodash';

// Assets and styles
import accordionStyles from '~styles/accordionStyles.scss';

import { formatTime } from '~utils/common.utils';

// Components
import TaskTextFields from './TaskTextFields';
import TaskListItemActionButtons from './TaskListItemActionButtons';
import ActionButtons from './ActionButtons';
import TransferInfoRow from './TransferInfoRow';
import HistoryAccordion from './HistoryAccordion';
import Comments from './components/Comments';
import { ChevronButton, Spacing } from '~ui-kit';
import { lighterBlue } from '~theme';
import TaskStatus from './TaskStatus';

import { getUser } from '../../features/user/ducks';

const bind = classNames.bind(accordionStyles);

const getStatusText = entry => {
  switch (entry.type) {
    case 'ORDER_CONTENT_UPDATE':
      return 'Tilausta muutettu';
    default:
      return entry.status;
  }
};

const transformHistory = history =>
  history.map(entry => ({
    ...entry,
    text: getStatusText(entry),
  }));

// has task as props from parent
const TaskListAccordion = props => {
  const { task, alwaysOpen } = props;
  const [toggled, setToggled] = useState(alwaysOpen);

  const user = useSelector(getUser);

  const toggle = () => {
    setToggled(!toggled);
  };

  const panelDivStyles = bind({
    panel: true,
    show: toggled,
    taskListPanel: true,
  });

  const historyLog = transformHistory(task.progress);

  // Find the newest log entry that has `time`
  const statusTime = _.last(historyLog.filter(e => e.time)).time;

  return (
    <ListItem key={task.id}>
      {/* title and info comes from the order, this is designed
            based on general form */}
      <Header>
        {task.id} {task.categoryName}
        <TransferInfoRow task={task} user={user} />
      </Header>

      <TaskInfoWrapper>
        <SummaryFieldsContainer>
          <Spacing medium />

          {/* Summary fields are visible before accordion is open.. */}
          <TaskTextFields task={task} showAll={toggled} />
        </SummaryFieldsContainer>

        <TaskStatusWrapper>
          <TaskStatus
            progress={task.progress}
            time={statusTime ? formatTime(statusTime, true) : '-'}
            patientTransport={
              task.formType === 'CUSTOM_FORM_INTERNALPATIENTTRANSPORT'
            }
            handler={task.handler?.name}
          />
        </TaskStatusWrapper>
      </TaskInfoWrapper>

      <Spacing medium />

      {toggled && (
        <>
          <ActionButtons task={task} />
          <Spacing medium />
        </>
      )}

      {task.comments && toggled && (
        <>
          <Comments
            comments={task.comments}
            commentable={task.commentable}
            taskId={task.id}
          />
          <Spacing medium />
        </>
      )}

      {historyLog && toggled && historyLog.length > 0 && (
        <HistoryAccordion
          progress={task.progress}
          patientTransport={
            task.formType === 'CUSTOM_FORM_INTERNALPATIENTTRANSPORT'
          }
        />
      )}

      {/* accordion started ... */}
      {!toggled && !alwaysOpen && (
        <AccordionButton type="button" onClick={toggle}>
          <div className={accordionStyles.accordionButtonIcon}>
            <ChevronButton down />
          </div>
          Näytä lisätietoja
        </AccordionButton>
      )}
      {/* Panel div, visible while accordion clicked (toggled) */}

      <div className={panelDivStyles}>
        {/* buttons */}
        <TaskListItemActionButtons task={task} isApprover={user.isApprover} />

        {!alwaysOpen && (
          <AccordionButton type="button" onClick={toggle}>
            <div className={accordionStyles.accordionButtonIcon}>
              <ChevronButton up />
            </div>
            Piilota tiedot
          </AccordionButton>
        )}
      </div>
    </ListItem>
  );
};

const ListItem = styled.div`
  list-style-type: none;
  background: white;
  padding: 16px;
  margin: 16px 0;
  border-radius: 4px;
  border: 1px solid #cccccc;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 576px) {
    border-radius: 0px;
    // margin-right: -15px;
    // margin-left: -15px;
  }
`;

const TaskInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: space-between;
  padding-top: 8px;

  @media (max-width: 575px) {
    flex-direction: column-reverse;
  }
`;

const TaskStatusWrapper = styled.div`
  @media (max-width: 575px) {
    align-self: flex-end;
  }
`;

const Header = styled.h6`
  flex: 1;
  font-size: 16px;
  font-weight: 700;
  line-height: 1em;

  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: initial;
`;

const SummaryFieldsContainer = styled.div`
  margin: 10px 0 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const AccordionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background-color: transparent;
  cursor: pointer;
  padding: 0;
  width: 100%;
  border: none;
  text-align: left;
  outline: none;
  margin-bottom: 0px;
  margin-left: -6px;
  color: ${lighterBlue};
`;

export default TaskListAccordion;
